import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../config";
import { useStoreSettings } from "../contexts/StoreSettingsContext";
import { useLocalization } from "@/contexts/LocalizationContext";
import { useAppDispatch } from "../store/hooks";
import { setStoredPhoneNumber, setVerificationId } from "../store/authSlice";
import { AuthShell } from "@/components/AuthShell";

import {
  clearRecaptchaVerifier,
  sendOtpSms,
  storePhoneSession,
} from "../lib/firebase";

export default function Login() {
  const { t } = useLocalization();
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { storeSettings } = useStoreSettings();

  const [countryCode, setCountryCode] = useState("+852");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const RECAPTCHA_BUTTON_ID = "send-otp-button";
  const [recaptchaKey, setRecaptchaKey] = useState(0);

  // Optional: clear verifier when leaving page to avoid “already rendered” edge cases
  useEffect(() => {
    return () => {
      clearRecaptchaVerifier(RECAPTCHA_BUTTON_ID);
    };
  }, []);

  function toE164(cc: string, input: string) {
    const digits = input.replace(/\D/g, "");
    const ccDigits = cc.replace(/\D/g, "");
    if (!digits) return "";
    return `+${ccDigits}${digits}`;
  }

  const handleSendOTP = async (e: React.FormEvent) => {
    e.preventDefault();

    const formattedPhone = toE164(countryCode, phoneNumber);
    if (!formattedPhone) {
      setError(t("auth.login.phonePlaceholder"));
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const confirmation = await sendOtpSms({
        phoneNumberE164: formattedPhone,
        recaptchaButtonId: RECAPTCHA_BUTTON_ID,
        // Optional localization (if you want):
        // useDeviceLanguage: true,
      });

      // Store for app usage
      dispatch(setStoredPhoneNumber(formattedPhone));
      dispatch(setVerificationId(confirmation.verificationId));

      // Store for OTP page refresh safety
      storePhoneSession(formattedPhone, confirmation.verificationId);

      navigate(routes.otp);
    } catch (err: any) {
      console.error("OTP send error:", err);
      setRecaptchaKey(k => k + 1);
      let errorMessage = t("auth.errorMessages.otp.failed");
      if (err?.code === "auth/invalid-phone-number") {
        errorMessage = t("auth.errorMessages.otp.invalidPhoneNumber");
      } else if (err?.code === "auth/too-many-requests") {
        errorMessage = t("auth.errorMessages.otp.tooManyRequests");
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                position: "relative",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mx: 2,
                  color: "#2C3E50",
                  fontWeight: 600,
                  fontSize: "18px",
                  textAlign: "center",
                }}
              >
                {t("auth.login.title")}
              </Typography>
            </Box>

            <form onSubmit={handleSendOTP}>
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#2C3E50",
                    mb: 1,
                    fontWeight: 500,
                    fontSize: "14px",
                  }}
                >
                  {t("auth.login.phoneLabel")}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FormControl sx={{ minWidth: 80 }}>
                    <Select
                      value={countryCode}
                      onChange={e => setCountryCode(e.target.value)}
                      disabled={isLoading}
                      sx={{
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiSelect-select": { p: "10px" },
                      }}
                      IconComponent={KeyboardArrowDown}
                    >
                      <MenuItem value="+852" sx={{ textAlign: "left" }}>
                        +852
                      </MenuItem>
                      <MenuItem value="+1" sx={{ textAlign: "left" }}>
                        +1
                      </MenuItem>
                      <MenuItem value="+44" sx={{ textAlign: "left" }}>
                        +44
                      </MenuItem>
                      <MenuItem value="+91" sx={{ textAlign: "left" }}>
                        +91
                      </MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    type="tel"
                    placeholder={t("auth.login.phonePlaceholder")}
                    value={phoneNumber}
                    onChange={e => {
                      setPhoneNumber(e.target.value);
                      if (error) setError(""); // Clear error when user modifies input
                    }}
                    error={!!error}
                    disabled={isLoading}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        backgroundColor: "#FFF",
                        color: "#000000",
                        border: "1px solid #5B5B5B",
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "none",
                        },
                        "& .MuiInputBase-input": {
                          padding: 2,
                          height: "60px",
                          boxSizing: "border-box",
                        },
                      },
                      "& .MuiInputBase-input::placeholder": {
                        color: "#A0A0A0",
                        opacity: 1,
                      },
                    }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <Button
                  key={recaptchaKey}
                  id={RECAPTCHA_BUTTON_ID}
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || !phoneNumber}
                  sx={{
                    width: '100%',
                    height: '53px',
                    borderRadius: '8px',
                    padding: '10px 16px',
                    textTransform: 'none',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    "&:disabled": {
                      backgroundColor: storeSettings?.colorPalettes?.border || '#E0E0E0',
                      color: "#999",
                    },
                    fontSize: "14px",
                    fontWeight: 400,
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{ color: "white", mr: 1 }}
                      />
                      {t("auth.messages.sendingCode")}
                    </>
                  ) : (
                    t("common.continue")
                  )}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                  {error}
                </Alert>
              )}
            </form>
    </AuthShell>
  );
}
